import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { keyframes } from '@emotion/react'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt'
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Link,
  Pagination,
  Switch,
  Tab,
  Tabs,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { Theme } from '@mui/material/styles'
import DOMPurify from 'dompurify'

import { apiZodios } from '~api/apiZodios'
import { images } from '~assets/images'
import { Avatar, Button, Image } from '~components/common'
import { IS_DEMO, IS_PROD } from '~config'
import { darkTheme, lightTheme } from '~createTheme'
import {
  PreTextStreamResponseStep,
  StreamResponseStep,
  preTextStreamResponseSteps,
  useStreamAIResponse,
} from '~hooks/useStreamAIResponse'
import { RootState } from '~redux/rootReducer'
import {
  AIInteractionType,
  EnrichedOrganizationAISettings,
  QueryAnalysis,
  ResponseDisplayLink,
} from '~types'

import AISearchBar from './AISearchBar'
import ChatBubbleProgressIndicator from './ChatBubbleProgressIndicator'
import { ChatStreamProgressBubble } from './ChatStreamProgressBubble'
import { QueryAnalysisDebugInfo } from './QueryAnalysisDebugInfo'

const defaultBanner =
  'https://s3-us-east-2.amazonaws.com/polimorphic-sha256/assets/isometricCity.png'

interface SourcesProps {
  sources: ResponseDisplayLink[]
  maxSources?: number
  showTitle?: boolean
  testing?: boolean
  theme: Theme
  isDarkMode?: boolean
  orgAiSettings?: EnrichedOrganizationAISettings
}

const StyledLink = styled('a')({
  textDecoration: 'none',
  color: 'inherit',
  '&:hover': {
    textDecoration: 'underline',
  },
})

type LoadingState = 'analyzing' | 'retrieving' | 'generating' | 'waiting'

export const Sources: React.FC<SourcesProps> = ({
  sources,
  maxSources,
  showTitle = true,
  testing,
  theme,
  isDarkMode,
  orgAiSettings,
}) => {
  const displaySources = maxSources ? sources.slice(0, maxSources) : sources
  const uniqueSources = Array.from(
    new Map(displaySources.map((source) => [source.page_url, source])).values(),
  )

  return (
    <Box
      sx={{
        m: 3,
        ...(showTitle && { borderTop: '1px solid #888' }),
      }}
    >
      {showTitle && (
        <Typography
          variant='h3'
          fontFamily={orgAiSettings?.font_family || 'inherit'}
          sx={{
            margin: '20px 0 30px',
          }}
        >
          Sources
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {uniqueSources.map((source, index) => (
          <Link
            key={index}
            href={source?.page_gui_url || source?.page_url || undefined}
            target='_blank'
            underline='none'
            sx={{
              display: 'flex',
              alignItems: 'center',
              mt: index === 0 ? 0 : 2.5,
              color: theme.palette.text.primary,
              '&:hover': {
                bgcolor: theme.palette.aiSearch.hover,
              },
              cursor: 'pointer',
            }}
            className='double-blue-border-on-focus'
          >
            <Box sx={{ width: 'calc(100% - 45px)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  className='underline-on-hover'
                  fontFamily={orgAiSettings?.font_family || 'inherit'}
                  sx={{
                    ...theme.typography.link,
                    color: theme.palette.primary.main,
                    fontFamily: orgAiSettings?.font_family || 'inherit',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {source.title}
                </Typography>
                {source.external_link === false && (
                  <Chip
                    label='Hidden'
                    size='small'
                    title='Hidden sources will be used in the AI answer, but will not be shown in the sources list as a link'
                    sx={{ height: '20px' }}
                  />
                )}
                {source.external_source === false && (
                  <Chip
                    label='Private'
                    size='small'
                    title='Private sources will only be used in the AI answer if you are logged in and have access to the organization. External users will not see content/answers that use external sources'
                    sx={{ height: '20px' }}
                  />
                )}
              </Box>
              <Typography
                fontFamily={orgAiSettings?.font_family || 'inherit'}
                sx={{
                  mt: 1.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: theme.palette.text.primary,
                }}
              >
                {source.text}
              </Typography>
              {testing && (
                <Typography
                  fontFamily={orgAiSettings?.font_family || 'inherit'}
                  variant='caption'
                  sx={{ ml: 1, color: theme.palette.text.secondary }}
                >
                  Score: {source.relevance_score}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                alignItems: 'center',
                borderRadius: '50%',
                display: 'flex',
                height: '25px',
                justifyContent: 'center',
                ml: 1.25,
                mr: 2.5,
                width: '25px',
              }}
            >
              <OpenInNewIcon
                sx={{
                  width: '15px',
                  filter: isDarkMode ? 'brightness(0) invert(1)' : 'brightness(0)',
                }}
              />
            </Box>
          </Link>
        ))}
      </Box>
    </Box>
  )
}

const formatColor = (color: string | undefined): string => {
  if (!color) return ''
  return color.startsWith('#') ? color : `#${color}`
}

const bounceAnimation = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-4px);
  }
  60% {
    transform: translateY(-2px);
  }
`

const initialRenderAnimation = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`

interface UpdatedQueryViewProps {
  orgAiSettings: EnrichedOrganizationAISettings
  suggestions?: string[]
  isEmbedded?: boolean
  testing?: boolean
  initialQuery?: string
  isInline?: boolean
  showLogo?: boolean
}

interface SearchBoxProps {
  orgAiSettings: EnrichedOrganizationAISettings
  suggestions?: string[]
  testing?: boolean
  initialQuery?: string
  disableAnimations?: boolean
  isInline?: boolean
  showLogo?: boolean
  isMobile?: boolean
}

const postMessageScrollHeightOneLine = (): void =>
  window.parent.postMessage({ scrollHeight: document.body.scrollHeight }, '*')

export const SearchBox: React.FC<SearchBoxProps> = ({
  orgAiSettings,
  suggestions,
  testing,
  initialQuery,
  disableAnimations = false,
  isInline = false,
  showLogo = false,
  isMobile = false,
}) => {
  const emailIdentities = useSelector((state: RootState) => state?.organization.emailIdentities)
  const isAdmin = emailIdentities?.some(
    (identity) => identity.organization_id === orgAiSettings.organization_id,
  )
  const isSuper = useSelector((state: RootState) => state?.auth?.user?.super)

  // Derive theme from URL parameters
  const searchParams = new URLSearchParams(window?.location?.search || '')
  const isDarkMode = searchParams.get('theme') === 'dark'
  const theme = isDarkMode ? darkTheme : lightTheme
  const [query, setQuery] = useState<string>('')
  const [responseId, setResponseId] = useState<string | null>(null)
  const [response, setResponse] = useState<string>('')
  const [queryAnalysis, setQueryAnalysis] = useState<QueryAnalysis>()
  const [impression, setImpression] = useState<boolean | null>(null)
  const [responseSources, setResponseSources] = useState<ResponseDisplayLink[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isResponseComplete, setIsResponseComplete] = useState<boolean>(false)
  const [userId, setUserId] = useState<string>('')
  const [aiInteractionId, setAIInteractionId] = useState<string | undefined>(undefined)
  const [activeTab, setActiveTab] = useState(0)
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sources, setSources] = useState<ResponseDisplayLink[]>([])
  const [isLoadingSources, setIsLoadingSources] = useState(false)
  const [loadingState, setLoadingState] = useState<LoadingState>('analyzing')
  const [streamStep, setStreamStep] = useState<StreamResponseStep>()
  const [isStreamingInProgress, setIsStreamingInProgress] = useState(false)

  const isPreTextStreamStep = Boolean(
    streamStep && (preTextStreamResponseSteps as readonly string[]).includes(streamStep),
  )

  const [debugVisible, setDebugVisible] = useState<boolean>(true)
  const [showInitialAnimation, setShowInitialAnimation] = useState(false)
  const showDebugInfo = testing || (isSuper && debugVisible) || (!isSuper && !IS_DEMO && !IS_PROD)
  const pageSize = 10

  const poliApplySources =
    responseSources.filter((source) => source.source_type === 'SourcePoliProcess') || []
  const poliApplyUniqueSources =
    Array.from(new Map(poliApplySources.map((source) => [source.page_url, source])).values()) || []

  const loadTotalPages = async (searchQuery: string): Promise<void> => {
    const result = await apiZodios.ai_listSourcesCount({
      user_id: userId || undefined,
      organization_id: orgAiSettings.organization_id,
      query: searchQuery,
      threshold: 0.2,
    })
    setTotalPages(Math.ceil(result.count / pageSize))
  }

  const loadSources = async (searchQuery: string): Promise<void> => {
    setIsLoadingSources(true)
    try {
      const result = await apiZodios.ai_listSources({
        user_id: userId || undefined,
        organization_id: orgAiSettings.organization_id,
        query: searchQuery,
        offset: page * pageSize,
        limit: pageSize,
        threshold: 0.2,
      })
      setSources(result.sources)
    } finally {
      setIsLoadingSources(false)
    }
  }

  useEffect(() => {
    if (query === '' || activeTab !== 1) return
    loadSources(query)
  }, [page])

  useEffect(() => {
    postMessageScrollHeightOneLine()
  }, [response, responseSources, isLoading, activeTab, page])

  useEffect(() => {
    if (initialQuery) {
      handleSearch(initialQuery)
    }
  }, [initialQuery])

  const tabsRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLTextAreaElement>(null)
  const searchBoxRef = useRef<HTMLDivElement>(null)
  const responseContentRef = useRef<HTMLDivElement>(null)
  const loadingRef = useRef<HTMLDivElement>(null)

  const submitQuery = async (searchQuery: string): Promise<void> => {
    setIsLoading(true)
    if (!searchQuery.trim() || isLoading) return

    // Focus on loading message
    setTimeout(() => {
      loadingRef.current?.focus()
    }, 100)

    // NOTE: before we transition to streaming updates, display intermediate updates
    const retrievalTimer = setTimeout(() => {
      setLoadingState('retrieving')
    }, 2500)

    const generatingTimer = setTimeout(() => {
      setLoadingState('generating')
    }, 5000)

    const waitingTimer = setTimeout(() => {
      setLoadingState('waiting')
    }, 10000)

    try {
      loadTotalPages(searchQuery)
      loadSources(searchQuery)

      const result = await apiZodios.ai_generateResponse({
        user_id: userId || undefined,
        ai_interaction_id: aiInteractionId || undefined,
        organization_id: orgAiSettings.organization_id,
        interaction_type: AIInteractionType.enum.SEARCH,
        query: searchQuery || '',
      })
      setIsResponseComplete(true)
      clearTimeout(retrievalTimer)
      clearTimeout(generatingTimer)
      clearTimeout(waitingTimer)

      setTimeout(() => {
        setResponse(result.response_text)
        setResponseId(result.response_id)
        setQueryAnalysis(result.query_analysis)
        setUserId(result.user_id || '')
        setAIInteractionId(aiInteractionId || result.ai_interaction_id)
        setResponseSources(result.sources || [])

        setIsResponseComplete(false)
        setIsLoading(false)
        setTimeout(() => {
          const aiAnswerTab = tabsRef.current?.querySelector('[role="tab"]') as HTMLElement
          aiAnswerTab?.focus()
        }, 100)
      }, 500)
    } catch (error) {
      console.error('Error generating AI response:', error)
      setResponse('An error occurred while generating the response.')
      clearTimeout(retrievalTimer)
      clearTimeout(generatingTimer)
      clearTimeout(waitingTimer)
    }
  }

  const { isGeneratingResponseStream, streamAIResponse } = useStreamAIResponse(
    (response, receivedFirstResponseChunk) => {
      setStreamStep(response.step)

      // No response message to render
      if (response.step !== 'partial_response' && response.step !== 'complete_response') {
        return
      }

      // Set loading to false once we start receiving response text so it can be displayed
      if (!receivedFirstResponseChunk) {
        setIsLoading(false)
      }

      // Set the complete response text (streaming sends the full text each time)
      setResponse(response.response_text as string)

      if (isStreamingInProgress) {
        setIsStreamingInProgress(false)
      }

      // Update other response data when available
      if (response.response_id) {
        setResponseId(response.response_id)
      }
      if (response.query_analysis) {
        setQueryAnalysis(response.query_analysis)
      }
      if (response.user_id) {
        setUserId(response.user_id)
      }
      if (response.sources) {
        setResponseSources(response.sources)
      }

      // Mark as complete when streaming is done
      if (response.step === 'complete_response') {
        setIsResponseComplete(false)
        setTimeout(() => {
          const aiAnswerTab = tabsRef.current?.querySelector('[role="tab"]') as HTMLElement
          aiAnswerTab?.focus()
        }, 100)
      }
    },
    (errorMessage) => {
      setResponse(errorMessage)
      setIsLoading(false)
      setIsStreamingInProgress(false)
    },
  )

  const submitQueryStreaming = async (text: string): Promise<void> => {
    if (!text.trim()) return

    // Clear previous response for new streaming response
    setResponse('')
    setIsStreamingInProgress(true)

    // Load sources and pagination data
    try {
      loadTotalPages(text)
      loadSources(text)
    } catch (error) {
      console.error('Error loading sources:', error)
    }

    streamAIResponse({
      user_id: userId || undefined,
      ai_interaction_id: aiInteractionId || undefined,
      organization_id: orgAiSettings.organization_id,
      interaction_type: AIInteractionType.enum.SEARCH,
      query: text || '',
    })
  }

  const handleSearch = async (searchQuery: string): Promise<void> => {
    if (!searchQuery || searchQuery.length < 2) {
      setResponse(
        'It seems you have not entered a valid question. Please let me know what I can assist you with regarding ' +
          orgAiSettings.organization_name +
          '.',
      )
      setActiveTab(0)
      return
    }

    setQuery(searchQuery)
    setLoadingState('analyzing')
    setActiveTab(0) // Reset to AI Answer tab on new search
    setPage(0)
    setImpression(null)

    if (orgAiSettings.stream_chat_responses) {
      await submitQueryStreaming(searchQuery)
    } else {
      await submitQuery(searchQuery)
    }
  }

  const handleClear = (): void => {
    setQuery('')
    setResponse('')
    setImpression(null)
    setPage(0)
    setResponseSources([])
    setSources([])
    setActiveTab(0)
    setTimeout(() => {
      searchInputRef.current?.focus()
    }, 0)
  }

  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (contentRef.current) {
      const links = contentRef.current.getElementsByTagName('a')
      const eventHandlers: { link: HTMLAnchorElement; handler: (e: Event) => void }[] = []

      for (let i = 0; i < links.length; i++) {
        const link = links[i]
        const clickHandler = (e: Event): void => {
          e.preventDefault()
          const href = link.getAttribute('href')
          if (href) {
            window.open(href, '_blank', 'noopener,noreferrer')
          }
        }

        link.addEventListener('click', clickHandler)
        eventHandlers.push({ link, handler: clickHandler })
      }

      return (): void => {
        eventHandlers.forEach(({ link, handler }) => {
          link.removeEventListener('click', handler)
        })
      }
    }
  }, [response])

  useEffect(() => {
    if (initialQuery) {
      setQuery(initialQuery)
      void handleSearch(initialQuery)
    }
  }, [initialQuery])

  useEffect((): (() => void) | void => {
    if (responseId && !disableAnimations) {
      setShowInitialAnimation(true)
      const timer = setTimeout(() => setShowInitialAnimation(false), 2000)
      return () => clearTimeout(timer)
    }
  }, [responseId, disableAnimations])

  const processHtml = (html: string): string => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    // Make all links tabbable
    const links = doc.getElementsByTagName('a')
    for (let i = 0; i < links.length; i++) {
      const link = links[i]
      link.setAttribute('target', '_blank')
      link.setAttribute('rel', 'noopener noreferrer')
      link.setAttribute('tabindex', '0')
    }

    return DOMPurify.sanitize(doc.body.innerHTML)
  }

  return (
    <Box
      ref={searchBoxRef}
      sx={{
        outline: 'none',
        ...(isMobile && {
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          height: '100vh',
        }),
      }}
      aria-label='Polimorphic Search'
    >
      {!isInline && (
        <Box
          sx={{
            backgroundColor:
              formatColor(orgAiSettings.bg_color || '') || theme.palette.aiSearch.searchBackground,
            padding: '24px',
            color: formatColor(orgAiSettings.font_color || '#000000'),
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {showLogo && (orgAiSettings.organization_ai_logo || orgAiSettings.organization_image) && (
            <Box
              role='img'
              tabIndex={0}
              aria-label={`${orgAiSettings.organization_name} logo`}
              sx={{
                display: 'inline-block',
                marginRight: 2,
                '&:focus': {
                  outline: '2px solid rgb(8, 148, 207)',
                  outlineOffset: '2px',
                  borderRadius: '50%',
                },
              }}
            >
              <Avatar
                src={
                  orgAiSettings.organization_ai_logo ??
                  orgAiSettings.organization_image ??
                  undefined
                }
                size='40px'
              />
            </Box>
          )}
          <Typography
            variant='h6'
            fontFamily={orgAiSettings.font_family || 'inherit'}
            sx={{ fontSize: isMobile ? '18px' : '22px' }}
          >
            AI-Powered Search for <strong>{orgAiSettings.organization_name}</strong>
          </Typography>
        </Box>
      )}
      <AISearchBar
        onSearch={handleSearch}
        suggestions={suggestions}
        hasResults={response != null && response.trim() !== ''}
        query={query}
        onQueryChange={setQuery}
        inputRef={searchInputRef}
        orgAiSettings={orgAiSettings}
      />

      {(response || responseSources.length > 0) && (
        <Box sx={{ borderTop: `1px solid ${theme.palette.aiSearch.border}` }}>
          {showDebugInfo && queryAnalysis && (
            <QueryAnalysisDebugInfo queryAnalysis={queryAnalysis} orgAiSettings={orgAiSettings} />
          )}
          {isAdmin && (
            <Alert
              severity='info'
              sx={{
                mx: 3,
                mt: 2.5,
                backgroundColor: isDarkMode ? theme.palette.aiSearch.tabBackground : '',
                color: isDarkMode ? theme.palette.aiSearch.tabText : '',
                fontFamily: orgAiSettings.font_family || 'inherit',
              }}
            >
              You are logged into {orgAiSettings.organization_name} so you will see results and
              sources external users do not see.
            </Alert>
          )}
          <Box sx={{ display: 'flex', p: '10px 24px 0' }}>
            <Tabs
              ref={tabsRef}
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              aria-label='Search results tabs'
              TabScrollButtonProps={{
                tabIndex: -1, // Prevent scroll buttons from being tabbable
              }}
              sx={{
                '& .MuiTabs-scroller': {
                  '& [role="tab"]': {
                    '&:focus': {
                      borderBottom: '3px solid rgb(8, 148, 207)',
                    },
                  },
                },
              }}
            >
              <Tab
                label='AI Answer'
                sx={{
                  bgcolor: activeTab === 0 ? theme.palette.aiSearch.tabBackground : 'transparent',
                  color: isDarkMode ? `${theme.palette.aiSearch.tabText} !important` : 'inherit',
                  fontFamily: orgAiSettings.font_family || 'inherit',
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 'bold',
                }}
                aria-label='AI generated answer'
                role='tab'
                tabIndex={0}
              />
              {totalPages > 0 && (
                <Tab
                  label='All Results'
                  sx={{
                    bgcolor: activeTab === 1 ? theme.palette.aiSearch.tabBackground : 'transparent',
                    color: isDarkMode ? `${theme.palette.aiSearch.tabText} !important` : 'inherit',
                    fontFamily: orgAiSettings.font_family || 'inherit',
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 'bold',
                  }}
                  aria-label='All search results'
                  role='tab'
                  tabIndex={0}
                />
              )}
            </Tabs>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              onClick={handleClear}
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 'bold',
                textTransform: 'none',
                fontFamily: orgAiSettings.font_family || 'inherit',
              }}
            >
              Clear
            </Button>
          </Box>
        </Box>
      )}

      {isLoading && !isGeneratingResponseStream && !isPreTextStreamStep ? (
        <Box
          display='flex'
          flexDirection='column'
          justifyContent='center'
          alignItems='center'
          minHeight='150px'
          role='status'
          aria-live='assertive'
          tabIndex={0}
          ref={loadingRef}
        >
          <ChatBubbleProgressIndicator
            loadingState={loadingState}
            onComplete={isResponseComplete}
            orgAiSettings={orgAiSettings}
            showBubble={false}
          />
        </Box>
      ) : !response &&
        (isGeneratingResponseStream || isPreTextStreamStep || isStreamingInProgress) ? (
        <Box
          display='flex'
          flexDirection='column'
          justifyContent='center'
          alignItems='center'
          minHeight='150px'
          role='status'
          aria-live='assertive'
          tabIndex={0}
          ref={loadingRef}
        >
          <ChatStreamProgressBubble
            step={
              isGeneratingResponseStream
                ? 'initialization'
                : (streamStep as PreTextStreamResponseStep) || 'initialization'
            }
            orgAiSettings={orgAiSettings}
          />
        </Box>
      ) : (
        response && (
          <>
            {activeTab === 0 ? (
              <>
                <Box
                  ref={responseContentRef}
                  sx={{
                    p: 3,
                  }}
                  onLoad={postMessageScrollHeightOneLine}
                >
                  {poliApplyUniqueSources.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {poliApplyUniqueSources.map((source, index) => (
                        <StyledLink
                          key={index}
                          href={source?.page_gui_url || source?.page_url || undefined}
                          target='_blank'
                          rel='noopener noreferrer'
                          tabIndex={0}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                            border: `1px solid #${orgAiSettings.bg_color}`,
                            backgroundColor: `#${orgAiSettings.bg_color}22`,
                            borderRadius: '5px',
                            marginBottom: '20px',
                            padding: '15px',
                          }}
                        >
                          <Box sx={{ width: '24px', marginRight: '8px' }}>
                            <Image src={images.logo} size='24px' alt='Polimorphic logo' />
                          </Box>
                          <Typography
                            variant='body2'
                            fontFamily={orgAiSettings.font_family || 'inherit'}
                          >
                            Click to Apply Online: {source.title}
                          </Typography>
                        </StyledLink>
                      ))}
                    </Box>
                  )}
                  <Box
                    sx={{
                      fontFamily: orgAiSettings.font_family || 'inherit',
                      '& a': {
                        ...theme.typography.link,
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                        target: '_blank',
                        rel: 'noopener noreferrer',
                        fontFamily: 'inherit',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                        '&:focus-visible': {
                          boxShadow: `0 0 0 2px ${theme.palette.primary.main}, 0 0 0 4px #98e0ff`,
                        },
                      },
                      '& p': {
                        display: 'block',
                        margin: '1.5 0',
                        lineHeight: 1.6,
                        fontFamily: 'inherit',
                      },
                      '& li': {
                        marginBottom: 1,
                        lineHeight: 1.6,
                        fontFamily: 'inherit',
                      },
                      '& li p': {
                        display: 'inline',
                        margin: 0,
                        fontFamily: 'inherit',
                      },
                      '& ul, & ol': {
                        marginTop: 1.5,
                        marginBottom: 1.5,
                        paddingLeft: 3,
                      },
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        margin: '3 0 1.5',
                        lineHeight: 1.3,
                        fontFamily: 'inherit',
                      },
                      '& h1': { fontSize: 24 },
                      '& h2': { fontSize: 20 },
                      '& h3': { fontSize: 18 },
                      '& blockquote': {
                        borderLeft: '4px solid #e0e0e0',
                        margin: '2 0',
                        padding: '1 0 1 2',
                        fontStyle: 'italic',
                        color: 'text.secondary',
                        fontFamily: 'inherit',
                      },
                      '& code': {
                        backgroundColor: '#f5f5f5',
                        padding: '0.5 1',
                        borderRadius: '3px',
                        fontSize: 14,
                        fontFamily: 'monospace',
                      },
                      '& pre': {
                        backgroundColor: '#f5f5f5',
                        padding: 2,
                        borderRadius: '4px',
                        overflow: 'auto',
                        margin: '2 0',
                      },
                      '& pre code': {
                        padding: 0,
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    <div
                      ref={contentRef}
                      className='prose max-w-none'
                      dangerouslySetInnerHTML={{ __html: processHtml(response) }}
                      role='article'
                      aria-label='AI Response'
                    />
                  </Box>
                  {responseId && (
                    <Box
                      sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}
                      role='group'
                      aria-label='Feedback buttons'
                    >
                      <Typography
                        fontFamily={orgAiSettings.font_family || 'inherit'}
                        sx={{ color: theme.palette.text.secondary }}
                        tabIndex={0}
                      >
                        Was this answer helpful?
                      </Typography>
                      <IconButton
                        onClick={() => setImpression(impression === true ? null : true)}
                        color={impression === true ? 'success' : 'default'}
                        aria-label='Mark as helpful'
                        tabIndex={0}
                        sx={{
                          color: theme.palette.text.secondary,
                          ...(showInitialAnimation &&
                            !disableAnimations && {
                              animation: `${initialRenderAnimation} 2s ease-out`,
                            }),
                          ...(!disableAnimations && {
                            '&:hover': {
                              animation: `${bounceAnimation} 0.6s ease-in-out`,
                            },
                          }),
                        }}
                      >
                        <ThumbUpOffAltIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => setImpression(impression === false ? null : false)}
                        color={impression === false ? 'warning' : 'default'}
                        aria-label='Mark as not helpful'
                        tabIndex={0}
                        sx={{
                          color: theme.palette.text.secondary,
                          ...(showInitialAnimation &&
                            !disableAnimations && {
                              animation: `${initialRenderAnimation} 2s ease-out`,
                            }),
                          ...(!disableAnimations && {
                            '&:hover': {
                              animation: `${bounceAnimation} 0.6s ease-in-out`,
                            },
                          }),
                        }}
                      >
                        <ThumbDownOffAltIcon />
                      </IconButton>
                    </Box>
                  )}
                  {impression === true && (
                    <Alert
                      severity='success'
                      sx={{
                        mt: 1,
                        color: theme.palette.text.secondary,
                        backgroundColor: theme.palette.aiSearch.tabBackground,
                        fontFamily: orgAiSettings.font_family,
                      }}
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            orgAiSettings.upvote_custom_text || 'Glad we could help!',
                          ),
                        }}
                      />
                    </Alert>
                  )}
                  {impression === false && (
                    <Alert
                      severity='info'
                      sx={{
                        mt: 1,
                        color: theme.palette.text.secondary,
                        backgroundColor: theme.palette.aiSearch.tabBackground,
                        fontFamily: orgAiSettings.font_family,
                      }}
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            orgAiSettings.downvote_custom_text ||
                              'Sorry this answer was not helpful. Try wording your question differently. The more details the better. Or click through the sources listed.',
                          ),
                        }}
                      />
                    </Alert>
                  )}
                </Box>
                {responseSources.length > 0 && (
                  <Sources
                    sources={responseSources}
                    maxSources={5}
                    testing={testing}
                    theme={theme}
                    isDarkMode={isDarkMode}
                    orgAiSettings={orgAiSettings}
                  />
                )}
              </>
            ) : (
              <>
                <Sources
                  sources={sources}
                  maxSources={pageSize}
                  showTitle={false}
                  testing={testing}
                  theme={theme}
                  isDarkMode={isDarkMode}
                  orgAiSettings={orgAiSettings}
                />
                {isLoadingSources ? (
                  <Box mt={2} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : totalPages > 1 ? (
                  <Box
                    mt={2}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <Pagination
                      aria-label='Search results pagination'
                      count={totalPages}
                      page={page + 1}
                      onChange={(_, value) => setPage(value - 1)}
                      sx={{
                        fontFamily: orgAiSettings.font_family || 'inherit',
                        button: {
                          fontFamily: orgAiSettings.font_family || 'inherit',
                        },
                      }}
                    />
                  </Box>
                ) : null}
              </>
            )}
            {!isInline && orgAiSettings.search_url && (
              <NotFoundHelp
                websiteSearchUrl={orgAiSettings.search_url || ''}
                theme={theme}
                orgAiSettings={orgAiSettings}
              />
            )}
          </>
        )
      )}
      <SearchDisclaimer orgAiSettings={orgAiSettings} theme={theme} isMobile={isMobile} />
      {!isInline && (
        <SearchFooter
          isSuper={isSuper || false}
          debugVisible={debugVisible}
          onToggleDebug={setDebugVisible}
          theme={theme}
        />
      )}
    </Box>
  )
}

interface NotFoundHelpProps {
  websiteSearchUrl: string
  theme: Theme
  orgAiSettings?: EnrichedOrganizationAISettings
}

const NotFoundHelp: React.FC<NotFoundHelpProps> = ({ websiteSearchUrl, theme, orgAiSettings }) => {
  return (
    <Box
      sx={{
        m: 3,
        borderTop: '1px solid #888',
      }}
    >
      <Typography
        variant='h3'
        fontFamily={orgAiSettings?.font_family || 'inherit'}
        sx={{
          margin: '20px 0 30px',
        }}
      >
        Not finding what you are looking for?
      </Typography>
      <Typography
        fontFamily={orgAiSettings?.font_family || 'inherit'}
        sx={{
          fontSize: '16px',
          letterSpacing: '0.25px',
          lineHeight: '24px',
          mt: 1.875,
        }}
      >
        Try rephrasing your question, or being more specific. If your initial question was too broad
        or vague, try narrowing it down and providing more specific details. If you need critical or
        complex information that AI can't provide, consider contacting your local government
        directly.
      </Typography>
      <Box sx={{ display: 'flex', mt: 1.875 }}>
        <Link
          href={websiteSearchUrl}
          target='_blank'
          underline='none'
          sx={{
            width: 'calc(100% - 45px)',
            '&:hover': {
              bgcolor: theme.palette.aiSearch.hover,
            },
          }}
          className='double-blue-border-on-focus'
        >
          <Typography
            fontFamily={orgAiSettings?.font_family || 'inherit'}
            sx={{
              fontSize: '16px',
              letterSpacing: '0.25px',
              lineHeight: '24px',
              color: theme.palette.text.primary,
            }}
          >
            Want to try searching without AI?{' '}
            <span
              style={{
                color: theme.palette.primary.main,
                fontFamily: 'inherit',
                textDecoration: 'none',
              }}
            >
              Go to Website Search
            </span>
          </Typography>
        </Link>
      </Box>
    </Box>
  )
}

const SearchDisclaimer: React.FC<{
  orgAiSettings?: EnrichedOrganizationAISettings
  theme: typeof lightTheme
  isMobile?: boolean
}> = ({ orgAiSettings, theme, isMobile = false }) => {
  // HACK - Don't link back to Polimorphic.com for Morris County Surrogate
  const organizationId = orgAiSettings?.organization_id
  const morrisCountySurrogateOrganizationIdProd = 'ffa09fe1-aaea-4984-9e13-9fa483857e2b'
  const includePolimorphicLink = organizationId !== morrisCountySurrogateOrganizationIdProd

  if (isMobile) {
    return (
      <Typography
        variant='body2'
        sx={{
          textAlign: 'center',
          lineHeight: '20px',
          fontSize: '12px',
          p: 1,
          padding: '8px 24px',
        }}
      >
        {orgAiSettings?.disclaimer_text ||
          'DISCLAIMER: AI can make mistakes. Please confirm important information. Search history is stored in accordance with our '}
        <Button
          variant='link'
          href={'https://www.polimorphic.com/privacy-policy'}
          target='_blank'
          rel='noopener noreferrer'
          sx={{
            color: theme.palette.secondary.dark,
            fontWeight: 'bold',
            fontSize: '12px',
            letterSpacing: '0.25px',
          }}
        >
          Privacy Policy
        </Button>
        .{includePolimorphicLink ? ' ' : ''}
        {includePolimorphicLink ? (
          <Button
            variant='link'
            href={'https://polimorphic.com'}
            tabIndex={0}
            sx={{
              color: theme.palette.secondary.dark,
              textDecoration: 'none',
              fontSize: '12px',
              fontWeight: 'normal',
              letterSpacing: '0.25px',
              '&:hover': { textDecoration: 'none', color: theme.palette.text.primary },
              '&:focus': { textDecoration: 'underline' },
            }}
          >
            By Polimorphic.
          </Button>
        ) : (
          <Typography
            component='span'
            variant='body2'
            sx={{
              fontSize: '12px',
              color: theme.palette.secondary.dark,
              fontWeight: 'normal',
            }}
          >
            By Polimorphic.
          </Typography>
        )}
      </Typography>
    )
  }

  return (
    <Box
      sx={{
        textAlign: 'center',
        color: theme.palette.secondary.dark,
        fontSize: '14px',
        p: 1,
        padding: '8px 24px',
      }}
    >
      <Typography
        variant='body2'
        fontFamily={orgAiSettings?.font_family || 'inherit'}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(
            orgAiSettings?.disclaimer_text ||
              'DISCLAIMER: AI can make mistakes. Please confirm important information.',
          ),
        }}
      />
      <Typography variant='body2' fontFamily={orgAiSettings?.font_family || 'inherit'}>
        Search history is stored in accordance with our{' '}
        <Button
          variant='link'
          href={'https://www.polimorphic.com/privacy-policy'}
          target='_blank'
          rel='noopener noreferrer'
          sx={{
            color: theme.palette.secondary.dark,
            fontWeight: 'bold',
            fontSize: '14px',
            letterSpacing: '0.25px',
            fontFamily: 'inherit',
            top: '-2px',
          }}
        >
          Privacy Policy
        </Button>
        .{includePolimorphicLink ? ' ' : ''}
        {includePolimorphicLink ? (
          <Button
            variant='link'
            href={'https://polimorphic.com'}
            tabIndex={0}
            sx={{
              color: theme.palette.secondary.dark,
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: 'normal',
              fontFamily: 'inherit',
              letterSpacing: '0.25px',
              top: '-2px',
              '&:hover': { textDecoration: 'none', color: theme.palette.text.primary },
              '&:focus': { textDecoration: 'underline' },
            }}
          >
            By Polimorphic.
          </Button>
        ) : (
          <Typography
            component='span'
            variant='body2'
            fontFamily={orgAiSettings?.font_family || 'inherit'}
            sx={{
              fontSize: '14px',
              color: theme.palette.secondary.dark,
              fontWeight: 'normal',
              fontFamily: 'inherit',
            }}
          >
            By Polimorphic.
          </Typography>
        )}
      </Typography>
    </Box>
  )
}

const SearchFooter: React.FC<{
  isSuper?: boolean
  debugVisible?: boolean
  onToggleDebug?: (visible: boolean) => void
  theme: typeof lightTheme
}> = ({
  isSuper = false,
  debugVisible = false,
  onToggleDebug = (): void => {},
  theme,
}): React.ReactElement => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 3,
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {isSuper && (
          <Box mr={2}>
            <FormControlLabel
              control={
                <Switch
                  size='small'
                  checked={debugVisible}
                  onChange={(e) => onToggleDebug(e.target.checked)}
                  color='primary'
                  sx={{
                    '& .MuiSwitch-thumb': {
                      backgroundColor: theme.palette.primary.main,
                    },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    color: theme.palette.text.primary,
                  }}
                >
                  Debug Info
                </Typography>
              }
              sx={{ m: 0 }}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

const UpdatedQueryView: React.FC<UpdatedQueryViewProps> = ({
  orgAiSettings,
  suggestions,
  isEmbedded,
  testing,
  initialQuery,
  isInline,
  showLogo,
}) => {
  const searchParams = new URLSearchParams(window?.location?.search || '')
  const isDarkMode = searchParams.get('theme') === 'dark'
  const muiTheme = useTheme()
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'))

  // In inline mode, return only the search bar
  if (isInline) {
    return (
      <Box sx={{ width: '100%' }}>
        <SearchBox
          orgAiSettings={orgAiSettings}
          suggestions={suggestions}
          testing={testing}
          initialQuery={initialQuery}
          isInline={isInline}
          showLogo={showLogo}
          isMobile={isMobile}
        />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {!isEmbedded && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: isDarkMode
                ? darkTheme.palette.aiSearch.background
                : formatColor(orgAiSettings.bg_color || '#f0f0f0'),
              padding: '24px',
              color: isDarkMode
                ? darkTheme.palette.text.primary
                : formatColor(orgAiSettings.font_color || '#000000'),
              gap: 2,
            }}
          >
            {(orgAiSettings.organization_ai_logo || orgAiSettings.organization_image) && (
              <Box
                role='img'
                tabIndex={0}
                aria-label={`${orgAiSettings.organization_name} logo`}
                sx={{
                  display: 'inline-block',
                  '&:focus': {
                    outline: '2px solid rgb(8, 148, 207)',
                    outlineOffset: '2px',
                    borderRadius: '50%',
                  },
                }}
              >
                <Avatar
                  src={
                    orgAiSettings.organization_ai_logo ??
                    orgAiSettings.organization_image ??
                    undefined
                  }
                  size='32px'
                />
              </Box>
            )}
            <Typography
              fontFamily={orgAiSettings.font_family || 'inherit'}
              sx={{ fontSize: isMobile ? '16px' : '22px' }}
              tabIndex={0}
              fontWeight='bold'
            >
              {orgAiSettings.search_name || orgAiSettings.organization_name}
            </Typography>
          </Box>
          <img
            src={orgAiSettings.search_banner || defaultBanner}
            alt='Organization banner'
            style={{
              width: '100%',
              height: '200px',
              objectFit: 'cover',
            }}
          />
        </>
      )}

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          flexDirection: 'column',
          gap: 2,
          ...(isDarkMode && {
            backgroundColor: darkTheme.palette.aiSearch.background,
            color: darkTheme.palette.text.primary,
          }),
        }}
      >
        <Box
          sx={{
            width: '100%',
            alignItems: 'center',
          }}
        >
          <SearchBox
            orgAiSettings={orgAiSettings}
            suggestions={suggestions}
            testing={testing}
            initialQuery={initialQuery}
            isInline={isInline}
            showLogo={showLogo}
            isMobile={isMobile}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default UpdatedQueryView
